import React, { Component } from 'react';
type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost';
type ButtonSize = 'sm' | 'md' | 'lg';
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: React.ReactNode;
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  as?: 'button' | 'a';
  href?: string;
}
const variantClasses: Record<ButtonVariant, string> = {
  primary: 'bg-[#B08D57] text-[#1C1F23] hover:bg-[#C9A96E] active:bg-[#8E6F3E] border border-transparent font-semibold shadow-sm',
  secondary: 'bg-[#2F3A45] text-[#F7F7F5] hover:bg-[#3D4E5C] active:bg-[#1C1F23] border border-transparent font-semibold shadow-sm',
  outline: 'bg-transparent text-[#2F3A45] hover:bg-[#2F3A45] hover:text-[#F7F7F5] border border-[#2F3A45] font-semibold',
  ghost: 'bg-transparent text-[#B08D57] hover:bg-[#B08D57]/10 border border-transparent font-medium'
};
const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-4 py-2 text-sm rounded',
  md: 'px-6 py-3 text-sm rounded-md',
  lg: 'px-8 py-4 text-base rounded-md'
};
export function Button({
  variant = 'primary',
  size = 'md',
  children,
  isLoading = false,
  leftIcon,
  rightIcon,
  className = '',
  disabled,
  as: Component = 'button',
  href,
  ...props
}: ButtonProps) {
  const baseClasses = 'inline-flex items-center justify-center gap-2 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#B08D57] focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-body tracking-wide';
  const classes = [baseClasses, variantClasses[variant], sizeClasses[size], className].join(' ');
  if (Component === 'a' && href) {
    return <a href={href} className={classes} role="button" data-id="element-593">
        {leftIcon && <span aria-hidden="true" data-id="element-594">{leftIcon}</span>}
        {isLoading ? <span className="animate-spin" data-id="element-595">⟳</span> : children}
        {rightIcon && <span aria-hidden="true" data-id="element-596">{rightIcon}</span>}
      </a>;
  }
  return <button className={classes} disabled={disabled || isLoading} {...props} data-id="element-597">
      {leftIcon && <span aria-hidden="true" data-id="element-598">{leftIcon}</span>}
      {isLoading ? <span className="flex items-center gap-2" data-id="element-599">
          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true" data-id="element-600">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-601" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" data-id="element-602" />
          </svg>
          Loading...
        </span> : children}
      {rightIcon && !isLoading && <span aria-hidden="true" data-id="element-603">{rightIcon}</span>}
    </button>;
}